/**
 * use-pull-to-refresh.ts — Kéo danh sách xuống khi đang ở đầu để làm mới (kiểu Lark / ứng dụng di động).
 *
 * Gắn vào khung bao ngoài vùng cuộn (Radix ScrollArea: tìm `[data-radix-scroll-area-viewport]` để biết đã ở đầu chưa).
 * touchmove phải `passive: false` mới chặn được cuộn mặc định / "kéo-tải-lại" của trình duyệt khi đang kéo.
 */
import { useEffect, useRef, useState, type RefObject } from 'react'
import { inEdgeZone, pullDistance, shouldRefresh } from '@/lib/gestures'

export function usePullToRefresh(ref: RefObject<HTMLElement | null>, onRefresh: () => void | Promise<unknown>, enabled: boolean) {
  const [pull, setPull] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const refreshRef = useRef(onRefresh)
  refreshRef.current = onRefresh

  useEffect(() => {
    const el = ref.current
    if (!el || !enabled) return
    let startY: number | null = null
    let current = 0
    const viewport = () => el.querySelector<HTMLElement>('[data-radix-scroll-area-viewport]')

    const onStart = (e: TouchEvent) => {
      if (refreshing) return
      // Chạm xuống ở mép trái = dành cho cử chỉ mở ngăn kéo bộ lọc (hooks/use-edge-swipe.ts), không kéo-để-làm-mới.
      startY = (viewport()?.scrollTop ?? 0) <= 0 && !inEdgeZone(e.touches[0].clientX) ? e.touches[0].clientY : null
    }
    const onMove = (e: TouchEvent) => {
      if (startY == null) return
      const dy = e.touches[0].clientY - startY
      if (dy <= 0) { if (current) { current = 0; setPull(0) } return }
      // Đã cuộn xuống giữa chừng thì thôi (đầu danh sách không còn nữa).
      if ((viewport()?.scrollTop ?? 0) > 0) { startY = null; current = 0; setPull(0); return }
      if (e.cancelable) e.preventDefault()
      current = pullDistance(dy)
      setPull(current)
    }
    const onEnd = async () => {
      if (startY == null) return
      startY = null
      const go = shouldRefresh(current)
      current = 0
      if (!go) { setPull(0); return }
      setRefreshing(true)
      setPull(48)
      // Giữ vòng quay tối thiểu ~0,7s: lệnh làm mới (invalidate) trả về ngay, không có chút phản hồi nào thì nhìn như không ăn.
      try { await Promise.all([Promise.resolve(refreshRef.current()), new Promise((r) => setTimeout(r, 700))]) } finally { setRefreshing(false); setPull(0) }
    }
    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd)
    el.addEventListener('touchcancel', onEnd)
    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onEnd)
    }
  }, [ref, enabled, refreshing])

  return { pull, refreshing }
}
